// Things that move: reindeer and horses, chimney smoke, path lamps.
// WS-75 life: node materials; the smoke puffs are advanced by a compute pass (no per-frame loop over particles on the
// CPU), the smoke and lamp glows are pixel-sized sprites (gpu/sprites.js: WebGPU draws points one pixel wide).
import * as THREE from 'three/webgpu';
import { Fn, uniform, instanceIndex, instancedArray, instancedBufferAttribute, uv, vec3, vec4, float, sin, cos, select, smoothstep, length, pow } from 'three/tsl';
import { pointSprites, spriteMaterial, glowSprites } from './gpu/sprites.js';
import { inRing, stretchT } from './lines/measure.js'; // VIS-197: the shared inside test of every area; VIS-219: + the nearest point of a stretch
import { degraded } from './degraded.js'; // VIS-27: a quiet failure is reported
import { movers, RANK } from './giveway.js'; // give way: the rides give way to loose animals
import { lifePace } from './lifepace.js'; // VIS-267 step 3: a herd animal is posed only where it can be seen

const v3 = (x, y, z) => new THREE.Vector3(x, y, z);


/** Vertex-coloured merged geometry from [geometry, colour] pairs. */
function painted(parts) {
  const out = [];
  for (const [g0, col] of parts) {
    const g = g0.index ? g0.toNonIndexed() : g0;
    const c = new THREE.Color(col), n = g.attributes.position.count, arr = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { arr[i * 3] = c.r; arr[i * 3 + 1] = c.g; arr[i * 3 + 2] = c.b; }
    g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
    g.deleteAttribute('uv');
    out.push(g);
  }
  let count = 0;
  for (const g of out) count += g.attributes.position.count;
  const merged = new THREE.BufferGeometry();
  for (const name of ['position', 'normal', 'color']) {
    const arr = new Float32Array(count * 3);
    let o = 0;
    for (const g of out) { arr.set(g.attributes[name].array, o); o += g.attributes[name].array.length; }
    merged.setAttribute(name, new THREE.BufferAttribute(arr, 3));
  }
  return merged;
}

const at = (g, x, y, z, rx = 0, ry = 0, rz = 0) => { g.rotateX(rx); g.rotateY(ry); g.rotateZ(rz); g.translate(x, y, z); return g; };

function animalGeometry(kind) {
  const reindeer = kind === 'reindeer';
  const coat = reindeer ? '#6e6259' : ['#7a4a2c', '#8c5a34', '#5e3a24'][Math.floor(Math.random() * 3)];
  const pale = reindeer ? '#ddd5c8' : '#d9c3a0';
  const s = reindeer ? 1 : 1.3;
  const parts = [
    [at(new THREE.CapsuleGeometry(0.34 * s, 0.95 * s, 4, 10), 0, 1.05 * s, 0, 0, 0, Math.PI / 2), coat],
    [at(new THREE.CapsuleGeometry(0.3 * s, 0.6 * s, 4, 8), -0.05, 0.95 * s, 0, 0, 0, Math.PI / 2), reindeer ? '#8a7d70' : coat],
    [at(new THREE.CylinderGeometry(0.16 * s, 0.24 * s, 0.75 * s, 8), 0.68 * s, 1.42 * s, 0, 0, 0, -0.75), reindeer ? pale : coat],
    [at(new THREE.CapsuleGeometry(0.13 * s, 0.4 * s, 4, 8), 0.98 * s, 1.72 * s, 0, 0, 0, -1.25), coat],
    [at(new THREE.SphereGeometry(0.07 * s, 6, 4), 1.22 * s, 1.6 * s, 0), '#1c1714'],
    [at(new THREE.ConeGeometry(0.07 * s, 0.4 * s, 6), -0.72 * s, 1.0 * s, 0, 0, 0, 1.9), reindeer ? pale : '#2a1a10'],
  ];
  if (reindeer) {
    // Branching antlers.
    for (const side of [1, -1]) {
      const rod = (a, b, r) => {
        const A = v3(...a), B = v3(...b), len = A.distanceTo(B);
        const g = new THREE.CylinderGeometry(r * 0.7, r, len, 5);
        g.translate(0, len / 2, 0);
        g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(v3(0, 1, 0), B.clone().sub(A).normalize()));
        g.translate(A.x, A.y, A.z);
        parts.push([g, '#cdb998']);
      };
      const base = [0.92, 1.92, 0.1 * side], mid = [0.7, 2.45, 0.3 * side], tip = [0.95, 2.85, 0.42 * side];
      rod(base, mid, 0.04); rod(mid, tip, 0.03);
      rod(mid, [0.45, 2.75, 0.38 * side], 0.025);
      rod([0.82, 2.2, 0.2 * side], [1.1, 2.35, 0.18 * side], 0.025);
    }
  } else {
    parts.push([at(new THREE.BoxGeometry(0.5, 0.25, 0.08), 0.7, 1.85, 0, 0, 0, -0.75), '#2a1a10']); // mane
  }
  return { body: painted(parts), s };
}

/** Legacy primitive animal (the pre-WS-9 herd): body group + 4 swinging legs. Exported for the preview page. */
export function legacyAnimal(kind, mat, legMat, legGeo) {
  const { body, s } = animalGeometry(kind);
  const g = new THREE.Group();
  const bodyMesh = new THREE.Mesh(body, mat);
  bodyMesh.castShadow = true;
  g.add(bodyMesh);
  const legs = [];
  for (const [x, z] of [[0.48, 0.17], [0.48, -0.17], [-0.48, 0.17], [-0.48, -0.17]]) {
    const leg = new THREE.Mesh(legGeo, legMat);
    leg.position.set(x * s, 0.95 * s, z * s);
    leg.scale.set(s, 0.95 * s, s);
    leg.castShadow = true;
    g.add(leg); legs.push(leg);
  }
  return { g, legs, body: bodyMesh, s };
}
export function legacyMaterials() {
  return {
    mat: new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.9 }),
    legGeo: new THREE.CylinderGeometry(0.05, 0.065, 1, 5).translate(0, -0.5, 0),
    legMat: new THREE.MeshStandardNodeMaterial({ color: 0x2f2925, roughness: 0.9 }),
  };
}
/** Legacy pose: a.phase/a.walk/a.headDown drive leg swing and the body tip (unchanged behaviour). */
export function poseLegacy(a, dt, t, graze) {
  const swing = Math.sin(a.phase) * 0.5 * a.walk;
  a.legs[0].rotation.z = swing; a.legs[3].rotation.z = swing;
  a.legs[1].rotation.z = -swing; a.legs[2].rotation.z = -swing;
  // Grazing: lower the head by tipping the whole body slightly forward.
  a.headDown = THREE.MathUtils.lerp(a.headDown || 0, graze ? 1 : 0, Math.min(1, dt * 1.5));
  a.body.rotation.z = -0.12 * a.headDown + Math.sin(t * 1.3 + a.phase) * 0.01;
  a.body.position.y = Math.abs(Math.sin(a.phase)) * 0.04 * a.walk;
}

/**
 * Herds wandering inside their pens (walk / graze, plus idle for rigged models).
 * WS-9: a pen with `model: { url }` (resort.json `animalModels.<kind>`, relative to `base`) gets the rigged
 * glTF animal from animals.js (loaded on demand; the legacy primitives are the fallback if it fails).
 * Pens without a model keep the legacy primitive animals, so the default view is unchanged.
 */
// ---- WS-84 (begin): the shared animal models (web/models/animals, copies of the first package's approved reindeer and
// Finnhorse): any kind a package's `animalModels` lacks uses these, so every map can show reindeer and horses. A
// package's own model always wins (its own files and herd make-up).
const shared = (f, herd) => ({ url: new URL(`../models/animals/${f}.glb`, import.meta.url).href, ...(herd ? { herd } : {}) });
export const SHARED_ANIMALS = { reindeer: shared('reindeer', { bulls: 1 }), horse: shared('horse') };
/** The model a herd of `kind` uses: the package's (resort.json animalModels), else the shared one, else none (primitives). */
export const animalModel = (models, kind) => (models || {})[kind] || SHARED_ANIMALS[kind] || null;
// ---- WS-84 (end)
// WS-39: night level for rigged animals (main.js applyTime), forwarded once animals.js has loaded.
let animalsMod = null, nightLevel = 0;
export function setAnimalNight(n) { nightLevel = n; animalsMod?.setNightLevel(n); }

// ---- WS-41 roam areas (begin): where a herd may wander freely (outside winter, user round 7: "free the reindeer out
// from the paddocks ... within the lines which I drew"). A polygon minus blocked shapes (building footprints, props,
// water, fenced paddocks, each with its own margin), rasterised once at `res` m/cell on a canvas, so a point test is
// one lookup and a straight walk is checked cell by cell (an animal never crosses a building or a fence).
// blocked: [{ poly: [[x, z], ...], pad }] | [{ rect: { x, z, hx, hz, rot }, pad }] | [{ line: [[x, z], ...], pad }] (a line
// fence, `pad` m either side) | [{ dots: [x0, z0, x1, z1, ...], pad }] (tree trunks: TRUNK + `pad` m round each, one fill); roads are not blocked.
const TRUNK = 0.25;
export function roamArea({ polygon, blocked = [], res = 1 }) {
  const xs = polygon.map((p) => p[0]), zs = polygon.map((p) => p[1]);
  const x0 = Math.min(...xs) - 2, z0 = Math.min(...zs) - 2, w = Math.ceil((Math.max(...xs) + 2 - x0) / res), h = Math.ceil((Math.max(...zs) + 2 - z0) / res);
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  const ring = (pts) => { g.beginPath(); pts.forEach(([x, z], i) => (i ? g.lineTo((x - x0) / res, (z - z0) / res) : g.moveTo((x - x0) / res, (z - z0) / res))); g.closePath(); };
  g.fillStyle = '#fff'; ring(polygon); g.fill();
  g.fillStyle = g.strokeStyle = '#000'; g.lineJoin = 'round';
  for (const b of blocked) {
    if (b.dots?.length) { const R = (TRUNK + (b.pad || 0)) / res; g.beginPath(); for (let i = 0; i + 1 < b.dots.length; i += 2) { const u = (b.dots[i] - x0) / res, v = (b.dots[i + 1] - z0) / res; g.moveTo(u + R, v); g.arc(u, v, R, 0, 2 * Math.PI); } g.fill(); continue; }
    if (b.line?.length > 1) { g.beginPath(); b.line.forEach(([x, z], i) => (i ? g.lineTo((x - x0) / res, (z - z0) / res) : g.moveTo((x - x0) / res, (z - z0) / res))); g.lineWidth = Math.max(1, 2 * (b.pad || 0)) / res; g.stroke(); continue; }
    let pts = b.poly;
    if (b.rect) { const { x, z, hx, hz, rot = 0 } = b.rect, cs = Math.cos(rot), sn = Math.sin(rot); pts = [[-hx, -hz], [hx, -hz], [hx, hz], [-hx, hz]].map(([u, v]) => [x + u * cs + v * sn, z - u * sn + v * cs]); }
    if (!pts || pts.length < 3) continue;
    ring(pts); g.fill();
    if (b.pad > 0) { g.lineWidth = (2 * b.pad) / res; g.stroke(); }
  }
  const px = g.getImageData(0, 0, w, h).data, okm = new Uint8Array(w * h);
  for (let k = 0; k < w * h; k++) okm[k] = px[k * 4] > 200 ? 1 : 0;
  const ok = (x, z) => { const i = Math.floor((x - x0) / res), j = Math.floor((z - z0) / res); return i >= 0 && j >= 0 && i < w && j < h && okm[j * w + i] === 1; };
  const segOk = (a, b) => { const L = Math.hypot(b[0] - a[0], b[1] - a[1]), n = Math.max(1, Math.ceil(L / (res * 0.5))); for (let k = 0; k <= n; k++) if (!ok(a[0] + ((b[0] - a[0]) * k) / n, a[1] + ((b[1] - a[1]) * k) / n)) return false; return true; };
  let free = 0; for (let k = 0; k < w * h; k++) free += okm[k];
  /** a random free point; near = [x, z] + maxD: one of the few free points closest to `near` (within maxD) */
  const sample = (near = null, maxD = Infinity) => {
    const cand = [];
    for (let k = 0; k < 400 && cand.length < 60; k++) {
      const p = near && Number.isFinite(maxD) ? [near[0] + (Math.random() * 2 - 1) * maxD, near[1] + (Math.random() * 2 - 1) * maxD] : [x0 + Math.random() * w * res, z0 + Math.random() * h * res];
      if (ok(p[0], p[1])) cand.push(p);
    }
    if (!cand.length) return null;
    if (!near) return cand[0];
    cand.sort((p, q) => Math.hypot(p[0] - near[0], p[1] - near[1]) - Math.hypot(q[0] - near[0], q[1] - near[1]));
    return cand[Math.floor(Math.random() * Math.min(5, cand.length))];
  };
  return { ok, segOk, sample, area: free * res * res, polygon };
}
/** WS-41: the two sides of a fence gate: 2 m inside and 2.5 m outside the ring at gate.at (nearest edge). */
function gateSides(ringPts, at) {
  let best = null;
  for (let i = 0; i < ringPts.length; i++) {
    const [ax, az] = ringPts[i], [bx, bz] = ringPts[(i + 1) % ringPts.length], vx = bx - ax, vz = bz - az, L2 = vx * vx + vz * vz || 1;
    const t = stretchT(at[0], at[1], ax, az, bx, bz), px = ax + vx * t, pz = az + vz * t, d = Math.hypot(px - at[0], pz - at[1]); // VIS-219: the shared sum
    if (!best || d < best.d) { const L = Math.sqrt(L2); best = { d, px, pz, nx: -vz / L, nz: vx / L }; }
  }
  let { px, pz, nx, nz } = best;
  if (!inRing(px + nx * 1, pz + nz * 1, ringPts)) { nx = -nx; nz = -nz; } // n points into the paddock
  return { inner: [px + nx * 2, pz + nz * 2], outer: [px - nx * 2.5, pz - nz * 2.5] };
}
const segInRing = (a, b, ringPts) => { const n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / 0.5)); for (let k = 0; k <= n; k++) if (!inRing(a[0] + ((b[0] - a[0]) * k) / n, a[1] + ((b[1] - a[1]) * k) / n, ringPts)) return false; return true; };
// ---- WS-41 roam areas (end)

// VIS-267 step 3 (lifepace.js): every animal's logic runs every frame (its timers, mode, target, heading, where it walks,
// the give-way board), so it is always where it would have been; its pose (its object's place and turn, the clips, the
// legacy legs) only when the life pace says it can be seen, with all the time owed since its last pose (one coming into
// view is posed at once, its clips' phase moved on by that time). Hidden (a season, `off`): not asked, lifePace.away.
// The life pace's sphere and height per animal: the body's culling sphere (animals.js: 1.6 m round 0.8 m up) and how
// tall it stands (antlers, a raised head), times its scale.
const POSE_H = { horse: 1.9, reindeer: 1.8 }; // m (other kinds 1.6)
export function createHerds(scene, heightAt, pens, base = '', instancing = null, opts = {}) { // WS-34: instancing = instancing.js (optional); WS-41: opts.season
  const { mat, legGeo, legMat } = legacyMaterials();
  const animals = [];
  // VIS-267 step 3: an animal's object is put where it starts, and the life pace told that is where it is drawn (its last
  // posed spot, lifepace.js m.lifeAt): one that walks off out of view is posed away from there once the spot is in view
  const placed = (a, s) => {
    a.pr = 1.6 * s; a.pc = 0.8 * s; a.ph = (POSE_H[a.kind] || 1.6) * s;
    a.gx = a.pos.x; a.gz = a.pos.z; a.gy = heightAt(a.gx, a.gz); // the ground under it, sampled again once it walked 0.5 m
    a.g.position.set(a.gx, a.gy, a.gz); a.g.rotation.y = a.heading;
    a.lifeAt = [a.gx, a.gy + a.pc, a.gz];
  };

  // A pen may carry `solid` (main.js pensOf: LZ.roamArea of the pen, ok() = inside it and off the buildings, props, trunks
  // and line fences standing in it): its herd is never sampled on, and never steps into, a cabin in its paddock.
  const inPen = (x, z, pen, solid) => (solid ? solid.ok(x, z) : inRing(x, z, pen));
  function penSampler(pen, holes = null, solid = null) { // WS-84: `holes` = the pens inside this one (another herd's: never sampled)
    const xs = pen.map((p) => p[0]), zs = pen.map((p) => p[1]);
    const box = [Math.min(...xs), Math.max(...xs), Math.min(...zs), Math.max(...zs)];
    return () => {
      for (let k = 0; k < 200; k++) {
        const p = [box[0] + Math.random() * (box[1] - box[0]), box[2] + Math.random() * (box[3] - box[2])];
        if (inPen(p[0], p[1], pen, solid) && !holes?.some((h) => inRing(p[0], p[1], h))) return p;
      }
      return [(box[0] + box[1]) / 2, (box[2] + box[3]) / 2];
    };
  }
  // ---- WS-41 (begin): a pen may carry bySeason = { <season>: { pen, area } } (main.js, from paddocks[].animals.within
  // per season; area = roamArea, free roaming). The animal's current pen decides its targets and step checks.
  const samplers = new Map();
  const samplerOf = (pen, holes = null, solid = null) => { if (holes?.length || solid) return penSampler(pen, holes, solid); if (!samplers.has(pen)) samplers.set(pen, penSampler(pen)); return samplers.get(pen); }; // (WS-84: a pen with holes is not shared)
  const inHole = (a, x, z) => !!a.herd?.holes?.some((h) => inRing(x, z, h)); // WS-84: inside a pen that lies inside this herd's (two herds kept apart)
  const inSeason = (p, s) => !Array.isArray(p?.seasons) || p.seasons.includes(s); // WS-84: a free herd shows in its seasons only
  const roamPoint = (a) => { // wander locally (within ~50 m) along a clear straight line, so the herd stays spread and visible
    for (let k = 0; k < 40; k++) {
      const r = 8 + Math.random() * 42, th = Math.random() * Math.PI * 2, p = [a.pos.x + Math.cos(th) * r, a.pos.z + Math.sin(th) * r];
      if (a.area.ok(p[0], p[1]) && a.area.segOk([a.pos.x, a.pos.z], p) && !inHole(a, p[0], p[1])) return p; // (WS-84: + holes)
    }
    return [a.pos.x, a.pos.z];
  };
  const clearPoint = (a, sample) => { // a point of a solid pen it can walk to in a straight line (round a cabin, not into it)
    for (let k = 0; k < 20; k++) { const p = sample(); if (a.solid.segOk([a.pos.x, a.pos.z], p)) return p; }
    return sample();
  };
  const usePen = (a, cur) => { // cur = { pen, area, solid }
    a.pen = cur.pen; a.area = cur.area || null; a.solid = a.area ? null : cur.solid || null;
    const sample = a.area ? null : samplerOf(cur.pen, a.herd?.holes, a.solid); // (WS-84: + holes)
    a.randomPoint = a.area ? () => roamPoint(a) : a.solid ? () => clearPoint(a, sample) : sample;
  };
  const over = new Map(); // ---- WS-71: paddock id -> { pen, area } a herd was pointed at (update.repoint), in every season
  const startOf = (p) => over.get(p.id) || p.bySeason?.[opts.season] || (p.solid && p); // (a herd is a { pen, solid } itself)
  const spawnPoint = (cur, fallback) => (cur?.area ? cur.area.sample() || fallback() : fallback());
  // ---- WS-41 (end)
  function spawn(kind, pen, n, herd = null) {
    const randomPoint = penSampler(pen, herd?.holes, herd?.solid); // (WS-84: + holes)
    const cur = herd && startOf(herd); // WS-41
    for (let i = 0; i < n; i++) {
      const { g, legs, body, s } = legacyAnimal(kind, mat, legMat, legGeo);
      const p = spawnPoint(cur, randomPoint); // WS-41: (= randomPoint() without a season pen)
      const st = { g, legs, body, s, pen, randomPoint, pos: v3(p[0], 0, p[1]), heading: Math.random() * 6.28, target: cur ? null : randomPoint(), mode: 'graze', timer: Math.random() * 6, phase: Math.random() * 6, speed: kind === 'reindeer' ? 0.9 : 1.1, herd, kind };
      if (cur) { usePen(st, cur); st.target = st.randomPoint(); } // WS-41 (no season pen: the same Math.random order as before)
      placed(st, s); // VIS-267 step 3
      g.visible = inSeason(herd, season) && !herd?.off; // WS-84 (VIS-87: `off`, the herd of a ride whose place is hidden: lazy/places.js)
      scene.add(g);
      instancing?.addMeshes(g); // WS-34: primitive animals batched per material
      animals.push(st);
    }
  }
  // WS-9: rigged glTF herd (async; animals appear when the model has loaded).
  function spawnRigged(p) {
    const url = /^(https?:|\/)/.test(p.model.url) ? p.model.url : base + p.model.url;
    import('./animals.js').then(async (A) => {
      animalsMod = A; A.setNightLevel(nightLevel); // WS-39
      const model = await A.loadAnimalModel(url);
      if (p.dropped) return; // WS-84: taken away (Fine-tune) while its model loaded
      const randomPoint = penSampler(p.pen, p.holes, p.solid); // (WS-84: + holes)
      // Herd make-up (e.g. one bull, the rest cows / young / calves): resort.json animalModels.<kind>.herd.
      const roles = A.herdRoles(model.meta, p.count, p.model.herd || {});
      const cur = startOf(p); // WS-41: the season the page opens in may put the herd in a roam area
      for (let i = 0; i < p.count; i++) {
        const rig = A.createAnimal(model, A.herdVariation(model.meta, roles[i], Math.random, p.model.herd || {})); // WS-39: herd overrides (rug share)
        const q = spawnPoint(cur, randomPoint); // WS-41: (= randomPoint() without a season pen)
        rig.object.visible = inSeason(p, season) && !p.off; // WS-84 (VIS-87: off)
        scene.add(rig.object);
        instancing?.addRig(rig); // WS-34: one instanced draw for the whole herd
        const st = { g: rig.object, rig, pen: p.pen, randomPoint, pos: v3(q[0], 0, q[1]), heading: Math.random() * 6.28, target: cur ? null : randomPoint(), mode: 'graze', timer: Math.random() * 6, phase: 0, speed: (model.meta.herdSpeed || model.meta.walkSpeed || 0.9) * rig.info.scale, herd: p, kind: p.kind }; // (herdSpeed: a paddock pace slower than the walk clip's own: the horse)
        if (cur) { usePen(st, cur); st.target = st.randomPoint(); } // WS-41 (no season pen: the same Math.random order as before)
        animals.push(st);
        if (season !== opts.season && p.bySeason?.[season]) moveTo(st, p.bySeason[season], true); // WS-41: switched while loading
        placed(st, rig.info.scale); // VIS-267 step 3
      }
    }).catch((err) => { console.warn('animal model failed, using primitives:', err); degraded('animals', err, { kind: p.kind }); if (!p.dropped) spawn(p.kind, p.pen, p.count, p); });
  }
  let season = opts.season; // (WS-84: before the first spawn, which reads it)
  for (const p of pens) { if (p.model?.url) spawnRigged(p); else spawn(p.kind, p.pen, p.count, p); }

  // ---- WS-41 live season switch (begin): setSeason(s) gives every animal its pen for that season. An animal already
  // inside it keeps going. One that must leave (or enter) a fenced paddock walks through the paddock's gate when the
  // fence has one and the straight legs are clear; otherwise it is moved (main.js crossfades the frame = a soft fade):
  // released animals appear just outside their old paddock and disperse, returning ones anywhere in their pen.
  function moveTo(a, cur, instant = false) {
    const here = [a.pos.x, a.pos.z], from = a.area;
    usePen(a, cur);
    a.route = null;
    if (cur.area ? cur.area.ok(here[0], here[1]) : inPen(here[0], here[1], cur.pen, a.solid)) { a.target = a.randomPoint(); return 'stay'; }
    const ringPts = a.herd?.fence, gate = a.herd?.gates?.[0];
    if (!instant && ringPts && gate?.at) {
      const { inner, outer } = gateSides(ringPts, gate.at);
      if (cur.area && inRing(here[0], here[1], ringPts) && segInRing(here, inner, ringPts) && cur.area.ok(outer[0], outer[1])) { // out through the gate
        const t = cur.area.sample(outer, 20);
        if (t && cur.area.segOk(outer, t)) { a.route = [inner, outer, t]; a.mode = 'walk'; return 'walk'; }
      } else if (!cur.area && from && !inRing(here[0], here[1], ringPts) && from.segOk(here, outer)) { // back in through the gate
        const t = samplerOf(cur.pen, a.herd?.holes, a.solid)();
        if (segInRing(inner, t, ringPts)) { a.route = [outer, inner, t]; a.mode = 'walk'; return 'walk'; }
      }
    }
    const p = cur.area ? cur.area.sample(here, 30) || cur.area.sample(here, 70) || cur.area.sample(here, 150) || cur.area.sample() : samplerOf(cur.pen, a.herd?.holes, a.solid)();
    if (p) { a.pos.x = p[0]; a.pos.z = p[1]; }
    a.target = a.randomPoint(); a.mode = 'graze'; a.timer = 2 + Math.random() * 6;
    return 'move';
  }
  function setSeason(s) {
    season = s;
    const n = { stay: 0, walk: 0, move: 0 };
    for (const a of animals) { const cur = over.get(a.herd?.id) || a.herd?.bySeason?.[s]; if (cur) n[moveTo(a, cur)]++; } // WS-71: over
    for (const a of animals) if (Array.isArray(a.herd?.seasons)) a.g.visible = inSeason(a.herd, s); // WS-84: a free herd's seasons
    return n;
  }
  // ---- WS-71 (begin): Fine-tune's Life tool points the herd of paddock `pid` at { pen, area } live (main.js
  // LZ.roamArea), walking out through the gate when it can (moveTo); null = back to its pen of the season shown.
  function repoint(pid, cur) {
    if (cur) over.set(pid, cur); else over.delete(pid);
    for (const a of animals) if (a.herd?.id === pid) moveTo(a, cur || a.herd.bySeason?.[season] || a.herd); // (its own pen and solid)
  }
  // ---- WS-71 (end)
  // ---- WS-84 (begin): Fine-tune adds herds live (lazy/fine.js, which also takes them away and sets their holes, with
  // `animals` and `sampler`): add(pen) = a pen as main.js builds them; one dropped while its model loads stays away
  const add = (p) => { p.dropped = false; if (p.model?.url) spawnRigged(p); else spawn(p.kind, p.pen, p.count, p); };
  // ---- WS-84 (end)
  // ---- WS-41 (end)

  // VIS-267 step 3: the pose of one animal (clip = its rig's mode, v = its ground speed for the walk clip, legs = the
  // legacy legs' swing rate, graze = the legacy body tipped to graze), when lifePace says so, with the time owed
  function pose(a, clip, v, legs, graze, t) {
    if (!a.g.visible) { lifePace.away(a); return; }
    const x = a.pos.x, z = a.pos.z;
    if (Math.abs(x - a.gx) + Math.abs(z - a.gz) > 0.5) { a.gx = x; a.gz = z; a.gy = heightAt(x, z); }
    const d = lifePace.pose(a, x, a.gy + a.pc, z, a.pr, a.ph);
    if (!(d > 0)) return;
    a.g.position.set(x, a.gx === x && a.gz === z ? a.gy : heightAt(x, z), z);
    a.g.rotation.y = a.heading;
    if (a.rig) { a.rig.setMode(clip); a.rig.update(d, v); } else { a.phase += d * legs * a.walk; poseLegacy(a, d, t, graze); }
  }
  function update(dt, t) {
    const gw = movers.on; // give way: only while a ride runs (giveway.js)
    for (const a of animals) {
      // give way: a herd animal that can be in a ride's way (a free herd, or one walking through its paddock's gate; one
      // behind its paddock's fence cannot), where it was a step ago, on the movers board: the rides stop short of it
      if (gw && a.g.visible && (!a.herd?.fence || a.area || a.route)) movers.add(a.pos.x, a.pos.z, a.kind === 'horse' ? 0.9 : 0.7, RANK.animal);
      if (a.route) { // WS-41: walking out of / back into a paddock through its gate (no step check: the legs were checked)
        const [tx, tz] = a.route[0], dx = tx - a.pos.x, dz = tz - a.pos.z, d = Math.hypot(dx, dz);
        if (d < 0.6) { a.route.shift(); if (!a.route.length) { a.route = null; a.mode = 'graze'; a.timer = 3 + Math.random() * 5; } }
        else {
          const want = Math.atan2(-dz, dx), diff = ((want - a.heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
          a.heading += THREE.MathUtils.clamp(diff, -2.4 * dt, 2.4 * dt);
          const v = a.speed * 1.5 * dt * (a.rig ? Math.max(0.25, a.walk || 0) : 1) * Math.max(0, Math.cos(diff)); // turn first, then walk
          a.pos.x += Math.cos(a.heading) * v; a.pos.z -= Math.sin(a.heading) * v;
        }
        a.walk = THREE.MathUtils.lerp(a.walk || 0, a.route ? 1 : 0, Math.min(1, dt * 4)); // (the logic's pace too)
        pose(a, a.route ? 'walk' : 'graze', a.speed * 1.5 * a.walk, 9, false, t); // VIS-267 step 3
        continue;
      }
      a.timer -= dt;
      if (a.timer <= 0) {
        if (a.rig) { // rigged animals also stand alert (idle) now and then
          const r = Math.random();
          a.mode = a.mode === 'walk' ? (r < 0.65 ? 'graze' : 'idle') : (r < 0.55 ? 'walk' : r < 0.8 ? 'graze' : 'idle');
          a.timer = a.mode === 'walk' ? 4 + Math.random() * 8 : a.mode === 'idle' ? 3 + Math.random() * 5 : 6 + Math.random() * 10;
        } else {
          a.mode = a.mode === 'graze' ? 'walk' : (Math.random() < 0.6 ? 'graze' : 'walk');
          a.timer = a.mode === 'graze' ? 3 + Math.random() * 8 : 4 + Math.random() * 8;
        }
        if (a.mode === 'walk') a.target = a.randomPoint();
      }
      let moving = 0;
      if (a.mode === 'walk') {
        const dx = a.target[0] - a.pos.x, dz = a.target[1] - a.pos.z, d = Math.hypot(dx, dz);
        if (d < 0.8) { a.mode = 'graze'; a.timer = 3 + Math.random() * 6; }
        else {
          const want = Math.atan2(-dz, dx);
          let diff = ((want - a.heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
          a.heading += THREE.MathUtils.clamp(diff, -1.6 * dt, 1.6 * dt);
          const nx = a.pos.x + Math.cos(a.heading) * a.speed * dt * (a.rig ? a.walk || 0 : 1), nz = a.pos.z - Math.sin(a.heading) * a.speed * dt * (a.rig ? a.walk || 0 : 1);
          if ((a.area ? a.area.ok(nx, nz) : inPen(nx, nz, a.pen, a.solid)) && !inHole(a, nx, nz)) { a.pos.x = nx; a.pos.z = nz; moving = 1; } // WS-41: roam area; WS-84: never into a pen inside its own
          else a.target = a.randomPoint();
        }
      }
      a.walk = THREE.MathUtils.lerp(a.walk || 0, moving, Math.min(1, dt * 4)); // (the logic's pace too)
      pose(a, a.mode === 'walk' && moving ? 'walk' : a.mode === 'walk' ? 'idle' : a.mode, a.speed * a.walk, 7, a.mode === 'graze', t); // VIS-267 step 3
    }
  }
  update.setSeason = setSeason; // WS-41
  update.repoint = repoint; // WS-71
  Object.assign(update, { add, sampler: samplerOf }); // WS-84
  update.animals = animals; // WS-41: QA (positions, pens)
  return update;
}

/**
 * Soft rising smoke from chimneys and fires. WS-75: each puff's life is advanced on the GPU (a compute pass, renderer =
 * the map's WebGPURenderer) and its position is worked out in the vertex stage from its life, seed, source and the clock,
 * the same maths the CPU loop ran. material.uniforms = { scale, tint, alpha } as before (tint and alpha drive the look;
 * scale is kept for main.js but no longer needed: the sprite sizes itself by the canvas height, as scale did).
 * VIS-267 step 3 (the owner, 2026-10-26: "Chimney smoke can pause when the map rests"): the smoke never wakes the
 * resting map (no userData.travels); update() only owes the time, and drawn() (main.js: on a frame that will be drawn
 * and is no rest probe, before the render) moves the smoke's own clock (the drift, uT, and the puffs' compute step) on by
 * what is owed, at most 0.25 s: it stands still while the map rests and goes on without a jump when it draws again.
 */
export function createSmoke(scene, sources, renderer = null, camera = null) { // VIS-271 C7: camera = the compute pass is skipped while no source is in view
  const N = 26;
  const count = sources.length * N;
  const life = new Float32Array(count), seeds = new Float32Array(count), src = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) { life[i] = Math.random(); seeds[i] = Math.random(); }
  sources.forEach(([sx, sy, sz, strength = 1], s) => { for (let k = 0; k < N; k++) src.set([sx, sy, sz, strength], (s * N + k) * 4); });
  const U ={ scale: uniform(600), tint: uniform(new THREE.Color(0xdde3ea)), alpha: uniform(0.5) }, uDt = uniform(0), uT = uniform(0);
  const L = instancedArray(life, 'float'), S = instancedArray(seeds, 'float');
  // life += dt x 0.12 x (0.7 + seed x 0.6), wrapped past 1 (as the loop did: l - floor(l) = l - 1 under 2; VIS-271: a
  // step after time out of view can carry it past 2)
  const step = count ? Fn(() => {
    const l = L.element(instanceIndex).add(uDt.mul(0.12).mul(S.element(instanceIndex).mul(0.6).add(0.7)));
    L.element(instanceIndex).assign(select(l.greaterThan(1), l.sub(l.floor()), l));
  })().compute(count) : null;
  const mat = spriteMaterial();
  const soA = new THREE.InstancedBufferAttribute(src, 4), l = L.toAttribute(), sd = S.toAttribute(), so = instancedBufferAttribute(soA);
  mat.positionNode = vec3(
    so.x.add(l.mul(6).mul(so.w)).add(sin(uT.mul(0.6).add(sd.mul(9))).mul(l).mul(1.2)),
    so.y.add(l.mul(9).mul(so.w)),
    so.z.add(cos(uT.mul(0.5).add(sd.mul(7))).mul(l).mul(1.2)).sub(l.mul(2)));
  mat.sizeNode = l.mul(3.2).add(0.6);
  const d = length(uv().sub(0.5));
  mat.colorNode = vec4(U.tint, smoothstep(0, 0.5, d).oneMinus().mul(l.oneMinus()).mul(smoothstep(0, 0.15, l)).mul(U.alpha));
  mat.uniforms = U;
  const pts = pointSprites(null, mat, count);
  pts.name = 'smoke'; // (QA: docs/qa/ws42/ab_frozen.mjs --hide "...|smoke" leaves the puffs out: their random phases follow the load order)
  pts.userData.live = true; pts.userData.sources = sources; pts.userData.gpu = soA; // (gpu: the sources' copy the puffs start from; the editor rewrites it, lazy/finetune-carry.js fxMoved) WS-75: moved on the GPU (no vertex data changes on the CPU). VIS-267 step 3: it moves only on drawn pictures (drawn), so it is no waker (no userData.travels)
  if (count) scene.add(pts);
  // VIS-271 C7: each source's reach (puffs rise 9 x strength, drift 6 x strength east and 1.2 around, sprites ~4 m): while
  // the camera sees none of them the lives wait, and the next step in view takes the whole time owed (same puffs after)
  // (read each time: Fine-tune moves a building's sources, lazy/fine.js)
  const F = new THREE.Frustum(), PV = new THREE.Matrix4(), box = new THREE.Box3();
  const inView = () => {
    camera.updateMatrixWorld(); F.setFromProjectionMatrix(PV.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    for (let i = 0; i < sources.length; i++) { const s = sources[i], w = s[3] ?? 1; box.min.set(s[0] - 5.2, s[1] - 4, s[2] - 7.2); box.max.set(s[0] + 6 * w + 5.2, s[1] + 9 * w + 4, s[2] + 5.2); if (F.intersectsBox(box)) return true; }
    return false;
  };
  // VIS-267 step 3: hidden (a season, a QA harness) the lives wait as out of view, the time owed taken at the next step
  const waits = () => !pts.visible || (camera && !inView());
  let owed = 0, clock = 0; // clock: the resting smoke's own time (uT)
  return {
    material: mat, object: pts,
    update(dt) { if (count) owed += dt; }, // VIS-267 step 3: drawn() moves it on
    /** VIS-267 step 3: main.js, on a frame that will be drawn (no rest probe), before the render */
    drawn() {
      if (!count || waits()) return;
      const s = Math.min(owed, 0.25);
      owed = 0; clock += s;
      uT.value = clock; uDt.value = s;
      renderer?.compute(step);
    },
    info: () => ({ owed, t: uT.value, dt: uDt.value }), // QA (tests/unit/runtime/life-rest.test.mjs)
  };
}

/** Lamp posts every ~18 m along the resort's service roads, with glow sprites for the night. */
export function createLamps(scene, roads, heightAt, inside, M, { spacing = 18, offset = 3.6 } = {}) {
  const spots = [];
  for (const r of roads) {
    let carry = 0;
    for (let i = 0; i < r.pts.length - 1; i++) {
      const [x1, z1] = r.pts[i], [x2, z2] = r.pts[i + 1];
      const len = Math.hypot(x2 - x1, z2 - z1);
      const nx = -(z2 - z1) / len, nz = (x2 - x1) / len;
      for (let d = spacing - carry; d < len; d += spacing) {
        const t = d / len, x = x1 + (x2 - x1) * t + nx * offset, z = z1 + (z2 - z1) * t + nz * offset;
        if (inside(x, z)) spots.push([x, heightAt(x, z), z]);
      }
      carry = (carry + len) % spacing;
    }
  }
  const post = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.05, 0.07, 1.3, 6).translate(0, 0.65, 0), M.post, spots.length);
  const head = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.12, 0.12, 0.25, 8).translate(0, 1.38, 0), M.lamp, spots.length);
  const m = new THREE.Matrix4();
  spots.forEach(([x, y, z], i) => { m.makeTranslation(x, y, z); post.setMatrixAt(i, m); head.setMatrixAt(i, m); });
  post.castShadow = true;
  if (spots.length) scene.add(post, head); // WS-75: a zero-instance mesh is a WebGPU validation error (binding size zero): none on a map without road lamps
  // WS-75: glow sprites (gpu/sprites.js), one per lamp at 1.4 m; glow.userData.points = their centres (was glow.geometry's
  // position); material.uniforms = { k, scale } (k drives the glow; scale kept for main.js, the sprite sizes itself)
  const points = new THREE.InstancedBufferAttribute(new Float32Array(spots.flatMap(([x, y, z]) => [x, y + 1.4, z])), 3);
  const U = { k: uniform(0), scale: uniform(900) }, glow = glowSprites(points, vec3(1.0, 0.72, 0.4).mul(pow(smoothstep(0, 0.5, length(uv().sub(0.5))).oneMinus(), 2.2).mul(U.k)));
  glow.material.sizeNode = float(7);
  glow.material.uniforms = U;
  if (spots.length) scene.add(glow);
  return { glow, post, head, spots, count: spots.length }; // WS-34: post = static shadow caster (main.js shadow proxy); WS-58 head, spots: the lamps as items (lazy/lights.js)
}

